/**
 * System message addition for task delegation capability.
 * Only injected into the system prompt when the subagent toggle is enabled
 * AND the persona has subagentCapable: true.
 *
 * @returns The system message text describing how to use the delegate_subagent tool
 */
export function getSubagentSystemMessage(): string {
  return `

### Task Delegation

**Task delegation is ENABLED.**


#### Choosing between the two tools

Ask: **"Will I need the result to produce my response?"**

* **YES** → \`delegate_subagent\` (synchronous — result returned directly to you)
* **NO** → \`delegate_subagent_as_task\` (asynchronous — user retrieves it later via status card)

#### How delegate_subagent_as_task works

* Call with a clear \`instruction\` and optional \`context\`
* A status card appears showing running / completed / failed
* The user clicks "Check Status" to ask you to report progress, or "Check Result" to fetch the output — results do NOT appear automatically
* Sub-agent timeout: 15 minutes

#### REQUIRED: Decompose before you delegate

Before making any delegation decision, scan the request for independent units of work. If the request contains N items that don't depend on each other (write 10 haikus, summarize 6 documents, generate 4 charts), you MUST split them — one \`delegate_subagent\` call per item — and fire all calls in the same response so they run in parallel.

**Never send N independent items to a single sub-agent.** A single sub-agent that writes all 10 haikus is strictly worse than 10 sub-agents writing one each, because it runs serially inside the sub-agent and wastes the concurrency available to you.

| Request contains... | Action |
|---|---|
| N independent items | N parallel \`delegate_subagent\` calls, one item each |
| One multi-step task needed for your response | 1 \`delegate_subagent\` call |
| Fire-and-forget work the user retrieves later | \`delegate_subagent_as_task\` |
| Simple question, no tools needed | Answer directly |
`;
}
